/* eslint-disable react/no-array-index-key */
/* eslint-disable no-await-in-loop */
import React from 'react';
import PropTypes from 'prop-types';
import {withResizeDetector} from 'react-resize-detector';
import cloneDeep from 'lodash/cloneDeep';
/* Components */
import ModalDefault from '../../../common/modal/modal-default';
import WidgetEmpty from '../empty-widget-content/empty-widget-content';
import RssFeedItem from './rss-feed-item';
import isEqual from 'lodash/isEqual';
import Loader from '../../../common/loader/Loader';
import RssFeedErrorItem from './rss-feed-error-item';
import RssFeedForm from './RssFeedForm';
/* Helpers */
import {sortItemsByPropertyName} from '../../../../helpers/info-helpers';
import DashboardService from '../../../../services/dashboard-service';
/* Styles */
import './rss-feed.scss';
import {AUTO_REFRESH_OPTIONS, NUMBER_OF_ITEMS_OPTIONS} from '../widget-constants';
import {warning} from '../../../../helpers/notifications';
import WidgetHeader from '../widget-header/WidgetHeader';
import WidgetHeaderButton from '../widget-header/widget-header-button/WidgetHeaderButton';

class RSSFeedWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSettingsModalShown: false,
      rssItems: [],
      isLoadingData: false,
      errorUrls: [],
    };
  }

  componentDidMount() {
    this.isComponentMounted = true;
    this.fetchRssFeed();
    this.setAutoRefreshInterval();
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(prevProps.data.config.rss, this.props.data.config.rss) ||
      !isEqual(prevProps.data.config.feedNumberOptions, this.props.data.config.feedNumberOptions) ||
      prevProps.data.config.hideItemImage !== this.props.data.config.hideItemImage
    ) {
      this.fetchRssFeed();
    }
    if (this.shouldSetNewInterval(prevProps)) {
      this.setAutoRefreshInterval();
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;
    clearInterval(this.feedFetchInterval);
  }

  shouldSetNewInterval = (prevProps) => {
    if (!this.props.data.config.autoRefresh) {
      return false;
    }
    return (
      !prevProps.data.config.autoRefresh ||
      prevProps.data.config.autoRefresh.value !== this.props.data.config.autoRefresh.value
    );
  };

  isRssFeedSet = () => this.props.data.config.rss && this.props.data.config.rss.length;

  setAutoRefreshInterval = () => {
    clearInterval(this.feedFetchInterval);
    this.feedFetchInterval = setInterval(
      () => {
        this.fetchRssFeed();
      },
      this.props.data.config.autoRefresh
        ? this.props.data.config.autoRefresh.value
        : AUTO_REFRESH_OPTIONS[0].value
    );
  };

  fetchRssFeed = async () => {
    let config = cloneDeep(this.props.data.config);

    const itemsPerFeed = config?.feedNumberOptions || NUMBER_OF_ITEMS_OPTIONS[2];
    if (!this.isRssFeedSet()) {
      this.setState({
        rssItems: [],
      });
      return;
    }
    const newData = [];
    const newErrorUrls = [];
    const imageRegex = new RegExp(/<img.*?src="(.*?)"[^>]+>/, 'g');
    this.setState({
      isLoadingData: true,
      errorUrls: [],
      rssItems: [],
    });

    const rssFeedsArray = await Promise.all(
      this.props.data.config.rss.map((url) => DashboardService.getRssFeed([url]))
    );

    let rssFeeds = {};
    rssFeedsArray.forEach((rssFeed) => {
      const url = Object.keys(rssFeed)[0];
      const data = Object.values(rssFeed)[0];
      rssFeeds[url] = data;
    });

    for (const url in rssFeeds) {
      const data = rssFeeds[url];
      if (!data) {
        newErrorUrls.push(url);
        continue;
      }
      const defaultFeedImage = data.image ? data.image.url : null;
      const feedItemsSliced = data.items.slice(0, itemsPerFeed.value);
      feedItemsSliced.forEach((item) => {
        let rssItemImage = null;
        if (!config.hideItemImage) {
          imageRegex.lastIndex = 0;
          const imagesInContent = imageRegex.exec(item.content);
          const imageFromMediaGroup =
            item.mediaGroup &&
            item.mediaGroup.length &&
            item.mediaGroup[0]['media:content'] &&
            item.mediaGroup[0]['media:content'].length &&
            item.mediaGroup[0]['media:content'][0].$
              ? item.mediaGroup[0]['media:content'][0].$.url
              : null;
          const imageFromMediaContent =
            item.mediaContent &&
            item.mediaContent.length &&
            item.mediaContent[0].$ &&
            item.mediaContent[0].$.url
              ? item.mediaContent[0].$.url
              : null;
          const itemImage = imagesInContent && imagesInContent.length ? imagesInContent[1] : null;
          rssItemImage =
            imageFromMediaContent || imageFromMediaGroup || itemImage || defaultFeedImage;
        }
        newData.push({
          title: item.title,
          thumbnail: rssItemImage,
          description: item.contentSnippet,
          link: item.link,
          pubDate: new Date(item.pubDate).getTime(),
          source: data.title,
        });
      });
    }
    if (!this.isComponentMounted) {
      return;
    }
    if (!newData.length) {
      this.setState({
        isLoadingData: false,
        errorUrls: newErrorUrls,
        rssItems: [],
      });
      warning({message: 'There is no data for chosen RSS Feed'});
      return;
    }
    newData.sort(sortItemsByPropertyName('pubDate', false));
    this.setState({
      rssItems: newData,
      errorUrls: newErrorUrls,
      isLoadingData: false,
    });
  };

  toggleSettingsModal = (e) => {
    e.stopPropagation();
    this.setState({
      isSettingsModalShown: !this.state.isSettingsModalShown,
    });
  };

  handleSave = (data) => {
    const newData = {
      ...this.props.data,
      config: {
        ...data.config,
      },
      title: data.title,
    };
    this.setState(
      {
        isSettingsModalShown: false,
      },
      () => {
        this.props.onUpdate(newData);
      }
    );
  };

  render() {
    const rssItems = this.state.rssItems;
    return (
      <>
        <div className="glu-dashboard-card glu-content glu-widget-rss-feed">
          {this.state.isLoadingData && <Loader />}
          <WidgetHeader
            isEditAllowed={this.props.isEditAllowed}
            title={this.props.data.title}
            height={this.props.height}
            width={this.props.width}
            showWidgetInfo={this.props.showWidgetInfo(this.props.data)}
            openConfiguration={this.toggleSettingsModal}
            onRemove={this.props.onRemove(this.props.data)}
            onClone={this.props.onClone(this.props.data)}
          >
            <WidgetHeaderButton
              showButton={this.isRssFeedSet()}
              iconName="refresh"
              onClick={this.fetchRssFeed}
              hoverTooltip={'Refresh'}
            />
          </WidgetHeader>
          <div className="glu-content__body">
            {this.state.errorUrls.length
              ? this.state.errorUrls.map((url, index) => (
                  <RssFeedErrorItem
                    key={`${url}/${index}`}
                    url={url}
                    openConfiguration={this.toggleSettingsModal}
                    isEditAllowed={this.props.isEditAllowed}
                  />
                ))
              : null}
            {rssItems.length > 0 &&
              rssItems.map((item, index) => (
                <RssFeedItem key={`${item.title}/${index}`} item={item} />
              ))}
            {!rssItems.length > 0 && !this.state.errorUrls.length ? (
              <WidgetEmpty
                isEditAllowed={this.props.isEditAllowed}
                showSettingsModal={this.toggleSettingsModal}
                // text="The initial widget configuration is empty"
              />
            ) : null}
          </div>
        </div>
        {this.state.isSettingsModalShown && (
          <ModalDefault isOpen={this.state.isSettingsModalShown}>
            <div className="glu-modal-content glu-widget-rss-feed__modal">
              <div className="glu-modal__header">
                <div className="glu-modal__title glu-flex-grow">{this.props.data.title}</div>
                <div className="glu-flex-shrink">
                  <button
                    aria-label="close"
                    type="button"
                    className="glu-btn-link g-icon g-icon-close"
                    onClick={this.toggleSettingsModal}
                  />
                </div>
              </div>
              <RssFeedForm
                initialData={this.props.data}
                onFormSubmit={this.handleSave}
                onCancel={this.toggleSettingsModal}
              />
            </div>
          </ModalDefault>
        )}
      </>
    );
  }
}

RSSFeedWidget.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditAllowed: PropTypes.bool,
  onRemove: PropTypes.func,
  onUpdate: PropTypes.func,
  showWidgetInfo: PropTypes.func,
  onClone: PropTypes.func,
};

RSSFeedWidget.defaultProps = {
  isEditAllowed: false,
  onRemove: undefined,
  onUpdate: undefined,
  showWidgetInfo: undefined,
  onClone: undefined,
};

export default withResizeDetector(RSSFeedWidget);
